import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, Button, TextField, Typography, Grid, FormControl, Dialog, Checkbox,
  FormControlLabel,
  Switch, Snackbar,
  Alert,
  AlertProps, InputAdornment, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import { SalesLinesProps, SalesLineViewModel, SalesLineDocumentResultModel, Option, SalesHeaderProps, SalesDocumentViewModel, Payment, SalesDocumentAction, SalesDocumentActionResult, PrintAction, ReturnCandidateViewModel } from "../../Types/ExportTypes";
import { ToolbarWithSaveHandle } from "../../components/Toolbar/ToolbarWithSave";
import { useAuth, useLoading, useNavigation, useSnackbar } from "../../Context/Contexts";

import AsyncAutoComplete from "../../components/AsyncAutoComplete";
import {
  CustomerAutoComplete, CountryAutoComplete, CurrencyAutoComplete,
  PaymentTermsAutoComplete, SalespersonAutoComplete, LocationAutoComplete
} from "../../components/AutoComplete/AutoCompletes";
import SalesDocumentLines from "../../components/SalesDocument/SalesDocumentLines";
import PaymentLines from "../../components/SalesDocument/PaymentLines";
import SalesDocumentActions from "../../components/SalesDocument/SalesDocumentActions";
import { formatNumber } from '../../Utils/NumberUtils';
import InfoButton from "../../components/Tooltip/InfoTextField";
import { useTranslation } from "react-i18next";
import CenteredCard from "../Card/CenteredCardLayout";

import ReturnWizard from "../../components/Wizards/SalesReturn/ReturnWizard";
import ShippingAgentServiceSelect from "../../components/Shipping/ShippingAgentServiceSelect";
import OrderIntegrationsComponent from "./OrderIntegrationsComponent";
const SalesDocumentHeader: React.FC<SalesHeaderProps> = ({ title = "Edit Sales Document", documentNo, documentType, readUrl, updateUrl, deleteUrl, history = false, onLinesChanged, isDialog = false }) => {
  const { authService } = useAuth();
  const { goBack, navigate } = useNavigation();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const [loadedOptions, setloadedOptions] = useState(false);
  const { t } = useTranslation();

  const [salesDocument, setSalesDocument] = useState<SalesDocumentViewModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [modalOpenLines, setModalOpenLines] = useState(false); // Modal for SalesDocumentLines
  const [modalOpenFields, setModalOpenFields] = useState(false); // Modal for Additional Fields
  const [showBillingInfo, setShowBillingInfo] = useState(false);
  const [showShippingInfo, setShowShippingInfo] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [modalOpenPayments, setModalOpenPayments] = useState(false);

  const [returnCandidate, setReturnCandidate] = useState<ReturnCandidateViewModel | null>(null);

  const [integrationDialogOpen, setIntegrationDialogOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const cancelWizard = () => setWizardOpen(false);

  const handleIntegrationCountChange = useCallback((count: number) => {
    setSalesDocument((prev) => {
      if (!prev || prev.integrations === count) return prev;
      return { ...prev, integrations: count };
    });
  }, []);

  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);

  const hasPendingChanges = () => (salesDocument?.propertiesChanged?.length ?? 0) > 0;

  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };

  const preventIfUnsaved = () => {
    if (hasPendingChanges()) {
      showHasChangesError();
      return true;
    }
    return false;
  };

  //const [snackbar, setSnackbar] = useState<Pick<AlertProps, "children" | "severity"> | null>(null);

  //const handleCloseSnackbar = () => setSnackbar(null);
  const setPaymentsOnDocument = (updateFn: (prevPayments: Payment[] | null) => Payment[]) => {
    setSalesDocument((prevDoc) => {
      if (!prevDoc) return null;
      return { ...prevDoc, payments: updateFn(prevDoc.payments ?? []) };
    });
  };

  const wrappedSetPayments = (newPayments: Payment[] | ((prevPayments: Payment[]) => Payment[])) => {
    setPaymentsOnDocument((prevPayments) =>
      typeof newPayments === "function" ? newPayments(prevPayments ?? []) : newPayments
    );
  };
  const togglePaymentsModal = () => {
    if (!modalOpenPayments && preventIfUnsaved()) return;
    setModalOpenPayments((prev) => !prev);
  };

  const toggleLinesModal = () => {
    if (!modalOpenLines && preventIfUnsaved()) return;
    setModalOpenLines((prev) => !prev);
  };
  const toggleFieldsModal = () => {
    if (!modalOpenFields && preventIfUnsaved()) return;
    setModalOpenFields((prev) => !prev);
  };

  const fetchSalesDocument = async (no: string) => {
    setIsLoading(true);
    try {
      const response = await authService.get<SalesDocumentViewModel>(readUrl);
      setSalesDocument(
        response
          ? { ...response, propertiesChanged: [], originalValues: { ...response } }
          : null
      );
    } catch (error: any) {
      showSnackbar(`${t("dialog.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
      if (documentType == 1) await tryFetchPostedSalesOrder(no);
    } finally {
      setIsLoading(false);
    }
  };

  const tryFetchPostedSalesOrder = async (orderNo: string) => {
    setIsLoading(true);
    try {
      const response = await authService.get<SalesDocumentViewModel>(`/salesDocumentApi/GetDocument/?documentType=${documentType}&documentNo=${documentNo}&open=false`);
      if (response) {
        navigate({ path: "/sales/history/document", params: { documentNo, documentType } });
      }
    } catch (error: any) {
      showSnackbar(`${t("dialog.fetchDataError")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);





  useEffect(() => {
    if (documentNo) fetchSalesDocument(documentNo);

  }, [documentNo]);

  //useEffect(() => {
  //    if (loadedOptions === false && (documentNo === "" || documentNo !== "" && salesDocument?.no === documentNo)) {
  //        setloadedOptions(true);
  //    }
  //}, [salesDocument, documentNo]);
  ;

  const handleSave = async () => {
    setIsLoading(true);
    try {
      var response = await authService.post<SalesDocumentViewModel>(updateUrl, salesDocument);
      setSalesDocument(
        response
          ? { ...response, propertiesChanged: [], originalValues: { ...response } }
          : null
      );
      showSnackbar(t("common.documentSaved"), "success");
    } catch (error: any) {
      showSnackbar(`${t("common.failedToSaveDocument")} : ${error.response?.data ?? error.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = async (key: keyof SalesDocumentViewModel, value: any) => {
    let newDocument: SalesDocumentViewModel | null = null;

    setSalesDocument((prev) => {
      if (!prev) return null;
      const updatedPropertiesChanged = prev.propertiesChanged ? [...prev.propertiesChanged] : [];


      const existingProperty = updatedPropertiesChanged.find((p) => p.key === key);

      if (existingProperty) {
        existingProperty.propertyFetched = false; // set so we on change refetch connected properties
      } else {
        updatedPropertiesChanged.push({ key, propertyFetched: false });
      }

      newDocument = { ...prev, [key]: value, propertiesChanged: updatedPropertiesChanged };
      return newDocument;
    });

    // Keys that require fetching new settings
    const requiresUpdate = ["currencyCode", "selltoCustomerNo", "billtoCustomerNo", "locationCode", "shopNo"];

    if (requiresUpdate.includes(key)) {
      try {
        setIsLoading(true);

        // Wait a tick to ensure state is updated
        await new Promise((resolve) => setTimeout(resolve, 0));

        if (!newDocument) return;

        const response = await authService.post<SalesDocumentViewModel>(
          "/orderApi/GetConnectedSettingsForChange",
          newDocument
        );

        if (response) {
          setSalesDocument((prev) => {
            if (!prev) return null;
            return {
              ...response,
              payments: prev.payments,
              originalValues: prev.originalValues,
              propertiesChanged: prev.propertiesChanged,
            };
          });
        }
      } catch (error: any) {
        console.error("Failed to fetch connected settings:", error);
        showSnackbar(`${t("common.failedToFetchSettings")} : ${error.response?.data ?? error.message}`, "error");

      } finally {
        setIsLoading(false);
      }
    }
  };


  const handleChanges = (changes: Partial<SalesDocumentViewModel>) => {
    setSalesDocument((prev) => {
      if (!prev) return null;

      const updatedPropertiesChanged = prev.propertiesChanged ? [...prev.propertiesChanged] : [];

      for (const key in changes) {
        const existingProperty = updatedPropertiesChanged.find((p) => p.key === key);

        if (existingProperty) {
          existingProperty.propertyFetched = false;
        } else {
          updatedPropertiesChanged.push({ key, propertyFetched: false });
        }
      }

      return {
        ...prev,
        ...changes,
        propertiesChanged: updatedPropertiesChanged
      };
    });
  };


  const handleCancelChanges = () => {
    //setIsLoading(true);
    try {
      setSalesDocument((prev) => {
        if (!prev || !prev.originalValues) return prev;

        const original = prev.originalValues;
        const changes: Partial<SalesDocumentViewModel> = {};


        for (const key in original) {
          const typedKey = key as keyof SalesDocumentViewModel;
          const originalValue = original[typedKey];
          const currentValue = prev[typedKey];

          if (originalValue !== currentValue) {
            changes[typedKey] = originalValue;
          }
        }

        if (Object.keys(changes).length === 0) return { ...prev, hasChanges: false };

        return {
          ...prev,
          ...changes,
          propertiesChanged: []
        };
      });
    } catch (error: any) {
      console.error("Failed to cancel changes:", error);
      showSnackbar(`${t("common.failedToCancelChanges")} : ${error.message}`, "error");
    } finally {
      // setIsLoading(false);
    }
  };

  const onPost = async () => {
    if (history) return;
    if (preventIfUnsaved()) return;
    if (!validateViewModel()) return;
    setIsLoading(true);
    try {
      console.log("Posting...");
      const body: SalesDocumentAction = {
        documentNo: salesDocument?.no,
        documentType: salesDocument?.documentType,
        open: (salesDocument?.xOrderStatus ?? 0) < 4,
        action: "Post"
      };
      const response = await authService.post<SalesDocumentActionResult>("/salesDocumentApi/DocumentAction", body);
      console.log("Post completed:", response);
      setIsLoading(false);
      navigate({ path: "/sales/history/document", params: { documentNo: response.result, documentType: 2 } });
    } catch (error: any) {
      console.error("Error during Post:", error);
      showSnackbar(`${t("common.failedToPost")} : ${error.response?.data ?? error.message}`, "error");
    }
    finally {
      setIsLoading(false);
    }
  };

  const onCancel = async () => {
    if (history) return;
    if (preventIfUnsaved()) return;
    if (!window.confirm(t("dialog.areYouSure"))) return;
    setIsLoading(true);
    try {
      console.log("Cancelling...");
      const body: SalesDocumentAction = {
        documentNo: salesDocument?.no,
        documentType: salesDocument?.documentType,
        open: (salesDocument?.xOrderStatus ?? 0) < 4,
        action: "Cancel"
      };
      const response = await authService.post<SalesDocumentActionResult>("/salesDocumentApi/DocumentAction", body);
      console.log("Cancel completed:", response);
      setIsLoading(false);
      navigate({ path: "/sales/order/list" });
    } catch (error: any) {
      console.error("Error during Cancel:", error);
      showSnackbar(`${t("common.failedToCancel")} : ${error.response?.data ?? error.message}`, "error");
    }
    finally {
      setIsLoading(false);
    }
  };

  const onCopy = async () => {
    if (preventIfUnsaved()) return;
    if (!window.confirm(t("dialog.areYouSure"))) return;
    setIsLoading(true);
    try {
      console.log("Copying...");
      const body: SalesDocumentAction = {
        documentNo: salesDocument?.no,
        documentType: salesDocument?.documentType,
        open: (salesDocument?.xOrderStatus ?? 0) < 4,
        action: "Copy"
      };
      const response = await authService.post<SalesDocumentActionResult>("/salesDocumentApi/DocumentAction", body);
      console.log("Copy completed:", response);
      setIsLoading(false);
      navigate({ path: "/sales/order/edit", params: { orderNo: response.result } });
    } catch (error: any) {
      console.error("Error during Copy:", error);
      showSnackbar(`${t("common.failedToCopy")} : ${error.response?.data ?? error.message}`, "error");
    }
    finally {
      setIsLoading(false);
    }
  };

  const onPrint = async () => {
    if (preventIfUnsaved()) return;
    setIsLoading(true);
    try {
      console.log("Printing...");
      const body: PrintAction = {
        documentNo: salesDocument?.no ?? "",
        documentType: salesDocument?.documentType ?? 0,
        history: (salesDocument?.xOrderStatus ?? 0) >= 4,
      };
      const response = await authService.post<Blob>("/printApi/PrintSalesDocument", body, "blob");

      showSnackbar(t("dialog.downloadSuccess"), "success");
      // Convert response Blob to object URL
      const url = window.URL.createObjectURL(response);
      window.open(url, "_blank");

      // Optional: Cleanup memory
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error: any) {
      console.error("Error during Print:", error);
      showSnackbar(`${t("common.failedToPrint")} : ${error.response?.data ?? error.message}`, "error");
    }
    finally {
      setIsLoading(false);
    }
  };

  const onSendEmail = async () => {
    if (preventIfUnsaved()) return;
    setIsLoading(true);
    try {
      console.log("Sending Email...");
      const body: SalesDocumentAction = {
        documentNo: salesDocument?.no,
        documentType: salesDocument?.documentType,
        open: (salesDocument?.xOrderStatus ?? 0) < 4,
        action: "SendEmail"
      };
      const response = await authService.post<SalesDocumentActionResult>("/salesDocumentApi/DocumentAction", body);
      console.log("SendEmail completed:", response);

      if (response?.result) {
        alert("Email sent successfully!");
      }
    } catch (error: any) {
      console.error("Error during SendEmail:", error);
      showSnackbar(`${t("common.failedToSendEmail")} : ${error.response?.data ?? error.message}`, "error");
    }
    finally {
      setIsLoading(false);
    }
  };



  const onIntegrate = async () => {
    if (!window.confirm(t("dialog.areYouSure"))) return;
    if (preventIfUnsaved()) return;
    if (!validateViewModel()) return;
    setIsLoading(true);
    try {
      const body: SalesDocumentAction = {
        documentNo: salesDocument?.no,
        documentType: salesDocument?.documentType,
        open: (salesDocument?.xOrderStatus ?? 0) < 4,
        action: "Integrate"
      };
      const response = await authService.post<SalesDocumentActionResult>("/salesDocumentApi/DocumentAction", body);
      showSnackbar(`${t("common.integrated")} ${documentNo}`, "success");
    } catch (error: any) {
      console.error("Error during integrate:", error);
      showSnackbar(`${t("common.failedToIntegrate")} : ${error.response?.data ?? error.message}`, "error");
    }
    finally {
      setIsLoading(false);
    }
  };


  const onReadyToShip = async () => {
    if (!window.confirm(t("dialog.areYouSure"))) return;
    if (preventIfUnsaved()) return;
    if (!validateViewModel()) return;
    setIsLoading(true);
    try {
      const body: SalesDocumentAction = {
        documentNo: salesDocument?.no,
        documentType: salesDocument?.documentType,
        open: (salesDocument?.xOrderStatus ?? 0) < 4,
        action: "readyToShip"
      };
      const response = await authService.post<SalesDocumentActionResult>("/salesDocumentApi/DocumentAction", body);
      showSnackbar(`${t("common.integrated")} ${documentNo}`, "success");
    } catch (error: any) {
      console.error("Error during integrate:", error);
      showSnackbar(`${t("common.saveErrorGeneric")} : ${error.response?.data ?? error.message}`, "error");
    }
    finally {
      setIsLoading(false);
    }
  };

  const SetOrderAsSettled = async () => {
    if (!window.confirm(t("dialog.areYouSure"))) return;
    if (preventIfUnsaved()) return;
    if (!validateViewModel()) return;
    setIsLoading(true);
    try {
      const body: SalesDocumentAction = {
        documentNo: salesDocument?.no,
        documentType: salesDocument?.documentType
      };
      await authService.post("/salesDocumentApi/SetOrderAndPaymentAsCaptured", body);
      showSnackbar(`${t("common.success")} ${documentNo}`, "success");
      setSalesDocument((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          settledExternal : true
        };
      });
    } catch (error: any) {
      console.error("Error during integrate:", error);
      showSnackbar(`${t("common.saveErrorGeneric")} : ${error.response?.data ?? error.message}`, "error");
    }
    finally {
      setIsLoading(false);
    }
  };




  const handleStartReturn = async () => {
    if (preventIfUnsaved()) return;
    showLoading();
    try {

      var model = await getReturnableModel(salesDocument?.no);
      console.log("model", model);
      setReturnCandidate(model);
      setWizardOpen(true);
    }
    catch (error: any) {
      console.error("Error during integrate:", error);
      showSnackbar(`${t("common.saveErrorGeneric")} : ${error.response?.data ?? error.message}`, "error");
    }
    finally {
      hideLoading();
    }
  };

  const getReturnableModel = async (documentNo?: string) => {
    if (!documentNo) return null;
    var url = "/salesReturnApi/GetReturnableModel?";
    if (documentType == 1) url += `orderNo=${documentNo}`;
    else if (documentType == 2) url += `invoiceNo=${documentNo}`;
    else return null;
    const response = await authService.get<ReturnCandidateViewModel>(
      url,
    );
    console.log("response", response);
    return response;
  }


  const validateViewModel = () => {
    if (!salesDocument) {
      return false;
    }
    if (!salesDocument.selltoCustomerNo?.trim() && !salesDocument.billtoCustomerNo?.trim()) {
      showSnackbar(t("dialog.missingCustomer"), "warning");
      return false;
    }
    if (!salesDocument.locationCode?.trim()) {
      showSnackbar(t("dialog.missingLocation"), "warning");
      return false;
    }




    return true;
  };

  if (!salesDocument) {
    return <Typography>Loading...</Typography>;
  }


  return (
    <>
      <Dialog open={wizardOpen} maxWidth="md" fullWidth>
        <Box p={3}>
          <ReturnWizard viewModel={returnCandidate} onSave={() => { console.log("hej"); cancelWizard(); }} cancelWizard={cancelWizard} />
        </Box>
      </Dialog>

      <Dialog open={integrationDialogOpen} onClose={() => setIntegrationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t("common.integrations")}</DialogTitle>
        <DialogContent dividers>
          {salesDocument?.no && (
            <OrderIntegrationsComponent
              documentNo={salesDocument.no}
              onCountChange={handleIntegrationCountChange}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntegrationDialogOpen(false)}>{t("common.cancel")}</Button>
        </DialogActions>
      </Dialog>

      <CenteredCard
        ref={toolbarRef}
        title={title}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={hasPendingChanges()}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: 2,
            gap: 1,
            flexWrap: { xs: "wrap", sm: "nowrap" }, // wrap only on mobile
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              flexShrink: 0, // don't stretch
            }}
          >
            <SalesDocumentActions
              onReadyToShip={//!history && documentType == 1 ? onReadyToShip : undefined
                undefined
              }
              onPost={!history ? onPost : undefined}
              onCancel={!history ? onCancel : undefined}
              onCopy={documentType == 1 ? onCopy : undefined}
              onPrint={onPrint}
              onSendEmail={onSendEmail}
              onIntegrate={onIntegrate}
              onCreateReturn={
                history && (documentType == 1 || documentType == 2)
                  ? handleStartReturn
                  : undefined
              }
              customActions={[
                ...((salesDocument?.documentType == 1 && salesDocument?.settledExternal == false && !history)
                  ? [
                    {
                      label: t("common.setOrderAsSettled"), 
                      onClick: () => SetOrderAsSettled(),
                    },
                  ]
                  : []),
              ]}
            />
          </Box>

          {/* Right side: integrations */}
          {salesDocument?.documentType === 1 && (salesDocument?.integrations ?? 0) > 0 && (
            <Box
              sx={{
                ml: { xs: 0, sm: "auto" },        
                width: { xs: "100%", sm: "auto" }, 
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setIntegrationDialogOpen(true)}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  "@keyframes pulse": {
                    "0%": { transform: "scale(1)", opacity: 1 },
                    "50%": { transform: "scale(1.05)", opacity: 0.9 },
                    "100%": { transform: "scale(1)", opacity: 1 },
                  },
                  animation: "pulse 3s infinite",
                }}
              >
                {t("common.integrations")} ({salesDocument?.integrations})
              </Button>
            </Box>
          )}
        </Box>





        {isLoading && <Typography>Loading...</Typography>}
        {/* Button to open SalesDocumentLines modal */}

        <Grid container spacing={2} >
          <Grid item xs={12} md={12}>
            <TextField
              label={documentType === 1 ? t("common.orderNo") :
                documentType == 2 ? t("common.invoiceNo") :
                  documentType == 3 ? t("common.creditNo") : t("common.documentNo")}
              value={salesDocument.no}
              onChange={(e) => handleChange("no", e.target.value)}
              fullWidth
              inputProps={{ maxLength: 20 }}
              disabled={!!documentNo || history}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">

              <CustomerAutoComplete
                key={(documentType == 1 ? salesDocument.selltoCustomerNo : salesDocument.billtoCustomerNo) || null}
                valueId={(documentType == 1 ? salesDocument.selltoCustomerNo : salesDocument.billtoCustomerNo) || null}
                onChange={(selected) => handleChange((documentType == 1 ? "selltoCustomerNo" : "billtoCustomerNo"), selected?.value || "")}
                label={
                  documentType == 1
                    ? t("common.sellToCustomerNo")
                    : t("common.billToCustomerNo")
                }
                disabled={history || salesDocument?.locked === true}
                infoText={
                  documentType == 1
                    ? t("help.sellToCustomerHelp")
                    : t("help.billToCustomerHelp")
                }
                error={!(documentType === 1 ? salesDocument.selltoCustomerNo : salesDocument.billtoCustomerNo)?.trim()}
                helperText={!(documentType === 1 ? salesDocument.selltoCustomerNo : salesDocument.billtoCustomerNo)?.trim() ? t("help.customerRequired") : ""}


              />

            </FormControl>


            {salesDocument.orderNo && (
              <Box
                component="a"
                href={`/sales/history/document?documentNo=${salesDocument.orderNo}&documentType=1`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textDecoration: "none", color: "inherit", display: "block" }}
              >
                <TextField
                  label={t("common.orderNo")}
                  value={salesDocument.orderNo}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                  InputProps={{ readOnly: true }}
                  sx={{
                    "& .MuiInputBase-input": {
                      textDecoration: "underline",
                      color: "blue",
                      cursor: "pointer",
                    },
                  }}
                />
              </Box>
            )}

            {salesDocument.invoices && (
              <>
                {salesDocument.invoices?.map((invoiceNo, index) => (
                  <>
                    <Box
                      component="a"
                      href={`/sales/history/document?documentNo=${invoiceNo}&documentType=2`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: "none", color: "inherit", display: "block" }}
                    >
                      <TextField
                        label={t("common.invoiceNo")}
                        value={invoiceNo}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        margin="normal"
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiInputBase-input": {
                            textDecoration: "underline",
                            color: "blue",
                            cursor: "pointer",
                          },
                        }}
                      />
                    </Box>

                  </>
                ))}
              </>
            )}
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={showBillingInfo}
                  onChange={(e) => setShowBillingInfo(e.target.checked)}
                />
              }
              label={t("common.showCustomerInfo")}
            />
          </Grid>

          {/* Billing Information Section */}
          {showBillingInfo && (
            <>
              <Box
                sx={{
                  backgroundColor: "secondary.main", // Light grey background
                  padding: 2,
                  borderRadius: 2,
                }}
              >
                <Grid item xs={12} >
                  <TextField
                    value={documentType == 1 ? salesDocument.selltoCustomerName : salesDocument.billtoName}
                    onChange={(e) => handleChange(documentType == 1 ? "selltoCustomerName" : "billtoName", e.target.value)}
                    fullWidth
                    margin="normal"
                    label={
                      documentType == 1
                        ? t("common.sellToCustomerName")
                        : t("common.billToCustomerName")
                    }
                    disabled={history || salesDocument?.locked === true}
                    inputProps={{ maxLength: 100 }}
                    helperText={
                      documentType == 1
                        ? t("help.sellToCustomerHelp")
                        : t("help.billToCustomerHelp")
                    }

                  />

                  <TextField
                    label={
                      documentType == 1
                        ? t("common.sellToCustomerName2")
                        : t("common.billToCustomerName2")
                    }
                    value={documentType == 1 ? salesDocument.selltoCustomerName2 : salesDocument.billtoName2}
                    onChange={(e) => handleChange(documentType == 1 ? "selltoCustomerName2" : "billtoName2", e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={history || salesDocument?.locked === true}
                    inputProps={{ maxLength: 100 }}
                  />

                  <TextField
                    label={
                      documentType == 1
                        ? t("common.sellToCustomerAddress")
                        : t("common.billToCustomerAddress")
                    }
                    value={documentType == 1 ? salesDocument.selltoAddress : salesDocument.billtoAddress}
                    onChange={(e) => handleChange(documentType == 1 ? "selltoAddress" : "billtoAddress", e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={history || salesDocument?.locked === true}
                    inputProps={{ maxLength: 100 }}
                  />

                  <TextField
                    label={
                      documentType == 1
                        ? t("common.sellToCustomerAddress2")
                        : t("common.billToCustomerAddress2")
                    }
                    value={documentType == 1 ? salesDocument.selltoAddress2 : salesDocument.billtoAddress2}
                    onChange={(e) => handleChange(documentType == 1 ? "selltoAddress2" : "billtoAddress2", e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={history || salesDocument?.locked === true}
                    inputProps={{ maxLength: 100 }}
                  />

                  <TextField
                    label={
                      documentType == 1
                        ? t("common.sellToCustomerPostCode")
                        : t("common.billToCustomerPostCode")
                    }

                    value={documentType == 1 ? salesDocument.selltoPostCode : salesDocument.billtoPostCode}
                    onChange={(e) => handleChange(documentType == 1 ? "selltoPostCode" : "billtoPostCode", e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={history || salesDocument?.locked === true}
                    inputProps={{ maxLength: 50 }}
                  />

                  <TextField
                    label={
                      documentType == 1
                        ? t("common.sellToCustomerCity")
                        : t("common.billToCustomerCity")
                    }
                    value={documentType == 1 ? salesDocument.selltoCity : salesDocument.billtoCity}
                    onChange={(e) => handleChange(documentType == 1 ? "selltoCity" : "billtoCity", e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={history || salesDocument?.locked === true}
                    inputProps={{ maxLength: 100 }}
                  />

                  <FormControl fullWidth margin="normal">
                    <CountryAutoComplete
                      key={documentType == 1 ? salesDocument.selltoCountryCode : salesDocument.billtoCountryCode || null}
                      valueId={documentType == 1 ? salesDocument.selltoCountryCode : salesDocument.billtoCountryCode || null}
                      onChange={(selected) => handleChange(documentType == 1 ? "selltoCountryCode" : "billtoCountryCode", selected?.value || "")}
                      label={
                        documentType == 1
                          ? t("common.sellToCountry")
                          : t("common.billToCountry")
                      }
                      disabled={history || salesDocument?.locked === true}
                    />
                  </FormControl>

                  <TextField
                    label={
                      documentType == 1
                        ? t("common.sellToCustomerContact")
                        : t("common.billToCustomerContact")
                    }
                    value={documentType == 1 ? salesDocument.selltoContact : salesDocument.billtoContact}
                    onChange={(e) => handleChange(documentType == 1 ? "selltoContact" : "billtoContact", e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={history || salesDocument?.locked === true}
                    inputProps={{ maxLength: 100 }}
                  />

                  <TextField
                    label={
                      documentType == 1
                        ? t("common.sellToCustomerMobile")
                        : t("common.billToCustomerMobile")
                    }
                    value={documentType == 1 ? salesDocument.xMobileNo : salesDocument.billtoMobile}  // Assuming xMobileNo is shared
                    onChange={(e) => handleChange(documentType == 1 ? "xMobileNo" : "billtoMobile", e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={history || salesDocument?.locked === true}
                    inputProps={{ maxLength: documentType == 1 ? 100 : 80 }}
                  />

                  <TextField
                    label={
                      documentType == 1
                        ? t("common.sellToCustomerEmail")
                        : t("common.billToCustomerEmail")
                    }
                    value={documentType == 1 ? salesDocument.sellToEmail : salesDocument.billToEmail}
                    onChange={(e) => handleChange(documentType == 1 ? "sellToEmail" : "billToEmail", e.target.value)}
                    fullWidth
                    margin="normal"
                    disabled={history || salesDocument?.locked === true}
                    inputProps={{ maxLength: 80 }}
                  />

                </Grid>
              </Box>
            </>
          )}
          <Grid item xs={12} >
            <FormControl fullWidth margin="normal">

              <PaymentTermsAutoComplete
                key={salesDocument.paymentTermsCode || null}
                valueId={salesDocument.paymentTermsCode || null}
                onChange={(selected) => handleChange("paymentTermsCode", selected?.value || "")}
                disabled={history || salesDocument?.locked === true}
                infoText={t("help.paymentTermsHelp")}
              />


            </FormControl>
            <FormControl fullWidth margin="normal">
              <CurrencyAutoComplete
                key={salesDocument.currencyCode || null}
                valueId={salesDocument.currencyCode || null}
                onChange={(selected) => handleChange("currencyCode", selected?.value || "")}
                label={t("common.currency")}
                disabled={history || salesDocument?.locked === true}
                infoText={t("help.currencyHelp")}
              />
            </FormControl>
            {documentType == 1 && (
              <TextField
                label={t("common.orderDate")}
                type="date"
                value={salesDocument.orderDate.split("T")[0]}
                onChange={(e) => handleChange("orderDate", e.target.value)}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                disabled={history || salesDocument?.locked === true}
              />
            )}
          </Grid>
          {/* Switch for Shipping Information */}

          {documentType == 1 && (
            <>
              <Grid item xs={12} >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showShippingInfo}
                      onChange={(e) => setShowShippingInfo(e.target.checked)}
                    />
                  }
                  label={t("common.showShippingInfo")}
                />
              </Grid>

              {/* Shipping Information Section */}
              {showShippingInfo && (
                <>
                  <Box
                    sx={{
                      backgroundColor: "secondary.main",
                      padding: 2,
                      borderRadius: 2,
                    }}
                  >
                    <Grid item xs={12} >
                      <TextField
                        label={t("common.shipToName")}
                        value={salesDocument.shiptoName}
                        onChange={(e) => handleChange("shiptoName", e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={history || salesDocument?.locked === true}
                        inputProps={{ maxLength: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipToNameHelp")} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label={t("common.shipToName2")}
                        value={salesDocument.shiptoName2}
                        onChange={(e) => handleChange("shiptoName2", e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={history || salesDocument?.locked === true}
                        inputProps={{ maxLength: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipToName2Help")} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label={t("common.shipToAddress")}
                        value={salesDocument.shiptoAddress}
                        onChange={(e) => handleChange("shiptoAddress", e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={history || salesDocument?.locked === true}
                        inputProps={{ maxLength: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipToAddressHelp")} />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <TextField
                        label={t("common.shipToAddress2")}
                        value={salesDocument.shiptoAddress2}
                        onChange={(e) => handleChange("shiptoAddress2", e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={history || salesDocument?.locked === true}
                        inputProps={{ maxLength: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipToAddress2Help")} />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <TextField
                        label={t("common.shipToMobile")}
                        value={salesDocument.shiptoMobile}
                        onChange={(e) => handleChange("shiptoMobile", e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={history || salesDocument?.locked === true}
                        inputProps={{ maxLength: 80 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipToMobileHelp")} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label={t("common.shipToEmail")}
                        value={salesDocument.shippingEmail}
                        onChange={(e) => handleChange("shippingEmail", e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={history || salesDocument?.locked === true}
                        inputProps={{ maxLength: 80 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipToEmailHelp")} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} >
                      <TextField
                        label={t("common.shipToCounty")}
                        value={salesDocument.shiptoCounty}
                        onChange={(e) => handleChange("shiptoCounty", e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={history || salesDocument?.locked === true}
                        inputProps={{ maxLength: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipToCountyHelp")} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label={t("common.shipToPostCode")}
                        value={salesDocument.shiptoPostCode}
                        onChange={(e) => handleChange("shiptoPostCode", e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={history || salesDocument?.locked === true}
                        inputProps={{ maxLength: 50 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipToPostCodeHelp")} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label={t("common.shipToCity")}
                        value={salesDocument.shiptoCity}
                        onChange={(e) => handleChange("shiptoCity", e.target.value)}
                        fullWidth
                        margin="normal"
                        disabled={history || salesDocument?.locked === true}
                        inputProps={{ maxLength: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipToCityHelp")} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <FormControl fullWidth margin="normal">
                        <CountryAutoComplete
                          valueId={salesDocument.shiptoCountryCode || null}
                          onChange={(selected) => handleChange("shiptoCountryCode", selected?.value || "")}
                          label={t("common.shipToCountry")}
                          disabled={history || salesDocument?.locked === true}
                          infoText={t("help.shipToCountryHelp")}
                        />

                      </FormControl>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={salesDocument.emailShipment}
                            onChange={(e) => handleChange("emailShipment", e.target.checked)}
                          />
                        }
                        label={t("common.emailShipment")}
                        disabled={history || salesDocument?.locked === true}
                      />
                      <TextField
                        label={t("common.shipmentDate")}
                        type="date"
                        value={salesDocument.shipmentDate.split("T")[0]}
                        onChange={(e) => handleChange("shipmentDate", e.target.value)}
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        disabled={history || salesDocument?.locked === true}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <InfoButton infoText={t("help.shipmentDateHelp")} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Box>
                </>
              )}
            </>
          )}

          <Grid item xs={12} >


            <FormControl fullWidth margin="normal">
              <LocationAutoComplete
                key={salesDocument.locationCode || null}
                valueId={salesDocument.locationCode || null}
                onChange={(selected) => handleChange("locationCode", selected?.value || "")}
                disabled={history || salesDocument?.locked === true}
                infoText={t("help.locationHelp")}
                error={!salesDocument.locationCode?.trim()}
                helperText={!salesDocument.locationCode?.trim() ? t("dialog.locationRequired") : ""}
              />
            </FormControl>
          </Grid>
          {documentType == 1 && (
            <>
              <Grid item xs={12} >
                <FormControl fullWidth margin="normal">
                  <ShippingAgentServiceSelect
                    key={`${salesDocument?.shippingAgentCode ?? ""}::${salesDocument?.shippingServiceCode ?? ""}`}
                    shippingAgentCode={salesDocument?.shippingAgentCode}
                    shippingServiceCode={salesDocument?.shippingServiceCode}
                    onChange={(agent, service) =>
                      handleChanges({ shippingAgentCode: agent, shippingServiceCode: service })
                    }
                    infoTextAgent={t("help.shippingAgent")}
                    infoTextService={t("help.shippingService")}
                    isLoading={isLoading}
                  />
                </FormControl>
              </Grid>
            </>
          )}
          {documentType != 3 && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showPayments}
                    onChange={(e) => setShowPayments(e.target.checked)}
                  />
                }
                label={t("common.showPayments")}
              />
            </Grid>
          )}
          {showPayments && (
            <>
              <Grid item xs={12}>
                <Box sx={{ padding: 3, borderRadius: 2 }}>
                  {/*<Typography variant="h6" sx={{ color: "black", mb: 2 }}>*/}
                  {/*    <Button variant="contained" onClick={togglePaymentsModal} sx={{ marginBottom: 2 }}>*/}
                  {/*        {t("common.showPayments")}*/}
                  {/*    </Button>*/}
                  {/*</Typography>*/}

                  {/*<Grid container spacing={2}>*/}
                  {/*    {salesDocument.payments?.map((payment, index) => (*/}
                  {/*        <Grid item xs={12} key={index}>*/}
                  {/*            <Box*/}
                  {/*                sx={{*/}
                  {/*                    backgroundColor: "background.paper",*/}
                  {/*                    padding: 2,*/}
                  {/*                    borderRadius: 2,*/}
                  {/*                    boxShadow: 2,*/}
                  {/*                }}*/}
                  {/*            >*/}
                  {/*                <Grid container spacing={2}>*/}
                  {/*                    <Grid item xs={12} sm={4}>*/}
                  {/*                        <TextField*/}
                  {/*                            label={t("common.paymentMethod")}*/}
                  {/*                            value={payment.paymentMethodDescription ?? ""}*/}
                  {/*                            disabled*/}
                  {/*                            fullWidth*/}
                  {/*                            margin="dense"*/}
                  {/*                            variant="outlined"*/}

                  {/*                        />*/}
                  {/*                    </Grid>*/}
                  {/*                    <Grid item xs={12} sm={4}>*/}
                  {/*                        <TextField*/}
                  {/*                            label={t("common.amount")}*/}
                  {/*                            value={payment.amount ?? 0}*/}
                  {/*                            disabled*/}
                  {/*                            fullWidth*/}
                  {/*                            margin="dense"*/}
                  {/*                            variant="outlined"*/}
                  {/*                        />*/}
                  {/*                    </Grid>*/}
                  {/*                    <Grid item xs={12} sm={4}>*/}
                  {/*                        <TextField*/}
                  {/*                            label={t("common.currency")}*/}
                  {/*                            value={payment.currencyCode ?? ""}*/}
                  {/*                            disabled*/}
                  {/*                            fullWidth*/}
                  {/*                            margin="dense"*/}
                  {/*                            variant="outlined"*/}
                  {/*                        />*/}
                  {/*                    </Grid>*/}
                  {/*                </Grid>*/}
                  {/*            </Box>*/}
                  {/*        </Grid>*/}
                  {/*    ))}*/}
                  {/*</Grid>*/}

                  <PaymentLines
                    documentNo={documentNo}
                    documentType={1}
                    readUrl="noneedlol"
                    updateUrl="orderApi/CreateOrUpdatePayment"
                    deleteUrl="orderApi/DeletePayment"
                    initialPayments={salesDocument?.payments}
                    setPaymentsOnDocument={wrappedSetPayments}
                    xMobileNo={salesDocument?.xMobileNo}
                    history={history}
                    locked={salesDocument?.locked}
                  />

                </Box>
              </Grid>
            </>
          )}

          <Box sx={{
            padding: "16px", width: "98vw",
            borderRadius: 2
          }}>
            <Grid item xs={12}>



              <SalesDocumentLines
                salesHeaderViewModel={salesDocument}
                readUrl={documentType == 1 ? "/orderApi/GetOrderLines" : "/salesDocumentApi/GetSalesDocumentLines"}
                updateUrl={documentType == 1 && !history ? "orderApi/UpdateOrderLine" : "NAN"}
                deleteUrl={documentType == 1 && !history ? "orderApi/DeleteOrderLine" : "NAN"}
                history={history}
                onLinesChanged={onLinesChanged}
                preventIfUnsaved={preventIfUnsaved}
                showHasChangesError={showHasChangesError}
                locked={salesDocument?.locked}
              />


            </Grid>
          </Box>
        </Grid>


        <Box sx={{ marginTop: 2, textAlign: "center" }}>

          {/* Button to open Additional Fields modal */}
          {/*<Button variant="outlined" onClick={toggleFieldsModal} sx={{ marginBottom: 2 }}>*/}
          {/*    Open Additional Fields*/}
          {/*</Button>*/}
          {isDialog == false && (
            <Button variant="outlined" onClick={goBack}>
              {t("common.back")}
            </Button>
          )}
        </Box>

        {/* Modal for SalesDocumentLines */}
        {/*<Modal open={modalOpenLines} onClose={toggleLinesModal}>*/}
        {/*    <Box*/}
        {/*        sx={{*/}
        {/*            padding: 4,*/}
        {/*            backgroundColor: "white",*/}
        {/*            borderRadius: "8px",*/}
        {/*            maxWidth: "90%",*/}
        {/*            width: "800px", // Fixed width*/}
        {/*            maxHeight: "90vh",*/}
        {/*            overflow: "auto",*/}
        {/*            margin: "auto",*/}
        {/*            mt: 4,*/}
        {/*        }}*/}
        {/*    >*/}
        {/*        <Typography variant="h6" gutterBottom>*/}
        {/*            Sales Document Lines*/}
        {/*        </Typography>*/}
        {/*        */}{/*<SalesDocumentLines*/}
        {/*        */}{/*    documentNo={salesDocument?.no}*/}
        {/*        */}{/*    documentType={1}*/}
        {/*        */}{/*    readUrl={"/orderApi/GetOrderLines"}*/}
        {/*        */}{/*    updateUrl={"taco"}*/}
        {/*        */}{/*    deleteUrl={"taco"}*/}
        {/*        */}{/*/>*/}
        {/*        <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>*/}
        {/*            <Button variant="contained" onClick={toggleLinesModal}>*/}
        {/*                Close*/}
        {/*            </Button>*/}
        {/*        </Box>*/}
        {/*    </Box>*/}
        {/*</Modal>*/}

        {/* Modal for Additional Fields */}
        <Dialog
          open={modalOpenFields}
          onClose={toggleFieldsModal}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: "8px" } }}
        >
          <Box sx={{ p: 4 }}>
            <Typography variant="h6">Additional Fields</Typography>
            <TextField
              label="Customer Discount"
              value={salesDocument.customerDiscount}
              onChange={(e) => handleChange("customerDiscount", parseFloat(e.target.value) || 0)}
              fullWidth
              margin="normal"
              disabled={history || salesDocument?.locked === true}
            />
            <TextField
              label="Customer invoiceDiscCode"
              value={salesDocument.invoiceDiscCode}
              onChange={(e) => handleChange("invoiceDiscCode", parseFloat(e.target.value) || 0)}
              fullWidth
              margin="normal"
              disabled={history || salesDocument?.locked === true}
              inputProps={{ maxLength: 10 }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={toggleFieldsModal}>
                {t("common.close")}
              </Button>
            </Box>
          </Box>
        </Dialog>
        <Dialog
          open={modalOpenPayments}
          onClose={togglePaymentsModal}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: "8px" } }}
        >
          <Box sx={{ p: 4 }}>

            <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}>
              <Button variant="contained" onClick={togglePaymentsModal}>
                {t("common.close")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      </CenteredCard>
    </>

  );
};

export default SalesDocumentHeader;
